package org.gymly.ui.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlyFont
import org.gymly.theme.GymlySpace
import org.gymly.util.userMessage

/**
 * Recuperación de contraseña en dos pasos: 1) correo → se envía un código de 6 dígitos;
 * 2) código + nueva contraseña. [scopes] son los tipos de cuenta que se prueban (STAFF, CLIENT).
 */
@Composable
fun PasswordRecoveryDialog(
    scopes: List<String>,
    initialEmail: String,
    request: suspend (scope: String, email: String) -> Unit,
    confirm: suspend (scope: String, email: String, code: String, newPassword: String) -> String,
    onDismiss: () -> Unit,
) {
    var email by remember { mutableStateOf(initialEmail) }
    var code by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var codeSent by remember { mutableStateOf(false) }
    var done by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    fun sendCode() {
        scope.launch {
            loading = true
            error = null
            try {
                scopes.forEach { request(it, email) }
                codeSent = true
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo enviar el código.")
            } finally {
                loading = false
            }
        }
    }

    fun changePassword() {
        if (newPassword.length < 8) {
            error = "La contraseña debe tener al menos 8 caracteres."
            return
        }
        scope.launch {
            loading = true
            error = null
            var message: String? = null
            var lastError: Throwable? = null
            for (accountScope in scopes) {
                try {
                    message = confirm(accountScope, email, code, newPassword)
                    break
                } catch (e: Throwable) {
                    lastError = e
                }
            }
            if (message != null) {
                done = message
            } else {
                error = lastError?.userMessage("Código incorrecto o vencido. Pide uno nuevo.")
                    ?: "Código incorrecto o vencido. Pide uno nuevo."
            }
            loading = false
        }
    }

    AlertDialog(
        onDismissRequest = { if (!loading) onDismiss() },
        title = { Text(if (done == null) "Recuperar contraseña" else "Listo") },
        text = {
            Column {
                done?.let { Text(it) }
                if (done == null && !codeSent) {
                    Text("Te enviaremos un código de 6 dígitos al correo de tu cuenta.", fontSize = GymlyFont.small)
                    Spacer(Modifier.height(GymlySpace.md))
                    GymlyTextField(
                        value = email,
                        onValueChange = { email = it },
                        placeholder = "Correo",
                        keyboardType = KeyboardType.Email,
                    )
                }
                if (done == null && codeSent) {
                    Text(
                        "Escribe el código que llegó a $email. Vence en 15 minutos.",
                        fontSize = GymlyFont.small,
                    )
                    Spacer(Modifier.height(GymlySpace.md))
                    GymlyTextField(
                        value = code,
                        onValueChange = { code = it.filter(Char::isDigit).take(6) },
                        placeholder = "Código de 6 dígitos",
                        keyboardType = KeyboardType.Number,
                    )
                    Spacer(Modifier.height(GymlySpace.sm))
                    GymlyTextField(
                        value = newPassword,
                        onValueChange = { newPassword = it },
                        placeholder = "Nueva contraseña (mínimo 8)",
                        isPassword = true,
                    )
                    PasswordRulesHint(newPassword)
                }
                error?.let {
                    Spacer(Modifier.height(GymlySpace.sm))
                    Text(it, color = GymlyColors.Error, fontSize = GymlyFont.small)
                }
            }
        },
        confirmButton = {
            when {
                done != null -> TextButton(onClick = onDismiss) { Text("Cerrar") }
                !codeSent -> GymlyPrimaryButton(text = "Enviar código", loading = loading, onClick = { sendCode() })
                else -> GymlyPrimaryButton(text = "Cambiar contraseña", loading = loading, onClick = { changePassword() })
            }
        },
        dismissButton = {
            when {
                done != null -> Unit
                codeSent -> TextButton(onClick = { sendCode() }, enabled = !loading) { Text("Reenviar código") }
                else -> TextButton(onClick = onDismiss) { Text("Cancelar") }
            }
        },
    )
}
